import {useState, useEffect, useRef} from 'react';
import {useDebounce} from './useDebounce';

export const DEBOUNCE_TIMEOUT = 250;

export const useGiphyData = (search) => {
  const [results, setResults] = useState([]);
  const [searchLinks, setSearchLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const giphyResultsRef = useRef(null);
  const pageRef = useRef(0);
  const isFetchingRef = useRef(false);

  const debouncedSearch = useDebounce(search, DEBOUNCE_TIMEOUT);

  const fetchResults = async (endpoint, append = false) => {
    try {
      if (!append) setLoading(true);
      setError(null);
      const res = await fetch(endpoint);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setResults((prev) => (append ? [...prev, ...data] : data));
    } catch (err) {
      setError('Failed to fetch GIFs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchLinks = async () => {
    try {
      const res = await fetch('/ajax/giphy/trending/searches/');
      if (!res.ok) throw new Error();
      setSearchLinks(await res.json());
    } catch (err) {
      evite.error('Error fetching search links:', err);
    }
  };

  const handleScroll = (e) => {
    const {scrollTop, scrollHeight, offsetHeight} = e.target;
    if (scrollTop > scrollHeight - offsetHeight * 3 && !isFetchingRef.current) {
      const nextPage = pageRef.current + 1;
      isFetchingRef.current = true;
      const endpoint = debouncedSearch
        ? `/ajax/giphy/search/${encodeURIComponent(debouncedSearch)}?page=${nextPage}`
        : `/ajax/giphy/trending?page=${nextPage}`;

      fetchResults(endpoint, true).then(() => {
        pageRef.current = nextPage;
        isFetchingRef.current = false;
      });
    }
  };

  useEffect(() => {
    fetchSearchLinks();
  }, []);

  useEffect(() => {
    const ref = giphyResultsRef.current;
    if (!ref) return () => {};
    ref.addEventListener('scroll', handleScroll);
    return () => ref.removeEventListener('scroll', handleScroll);
  }, [debouncedSearch]);

  useEffect(() => {
    pageRef.current = 0;
    const endpoint = debouncedSearch
      ? `/ajax/giphy/search/${encodeURIComponent(debouncedSearch)}?page=0`
      : '/ajax/giphy/trending?page=0';
    fetchResults(endpoint);
  }, [debouncedSearch]);

  return {giphyResultsRef, results, searchLinks, loading, error};
};
